import { useState } from "react";
import store from "../store";
import { setAuth } from "../store/slices/commonSlice";
import { Decrypt, Encrypt } from "./encryption";
import { useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { InfoIcon } from "../assets/images/svg/svg";

export const clearExistingToken = () => {
  const existingToken = localStorage.getItem("token");

  if (existingToken) {
    localStorage.removeItem("token");
    localStorage.clear();
    store.dispatch(setAuth(false));
  }
};

// export const formatAmount = (
//   amount,
//   decimal = null,
//   removeFloat = null,
//   compactFormat = "lakh" // If null, no compact formatting
// ) => {
//   let Amt = typeof amount === "string" ? parseFloat(amount) : amount;

//   if (isNaN(Amt)) return decimal ? "0" : "₹0.00";

//   const formatWithCommas = (amount) => {
//     const options = {
//       style: decimal ? "decimal" : "currency",
//       minimumFractionDigits: decimal && !removeFloat ? 2 : 0,
//       maximumFractionDigits: 2,
//       currency: !decimal ? "INR" : undefined,
//     };
//     return amount.toLocaleString("en-IN", options); // Adds commas and currency
//   };

//   const formatCompactAmount = (amount, format) => {
//     const removeTrailingZeroes = (value) => {
//       return parseFloat(value).toString();
//     };

//     // Compact formatting logic
//     if (format === "crore" || format === "10lakh" || format === "lakh") {
//       if (amount >= 10000000) {
//         // Convert to Crore
//         amount /= 10000000;
//         return decimal
//           ? removeTrailingZeroes(amount.toFixed(2)) + " Cr"
//           : "₹" + removeTrailingZeroes(amount.toFixed(2)) + " Cr";
//       } else if (amount >= 1000000) {
//         // Convert to 10 Lakhs
//         amount /= 1000000;
//         return decimal
//           ? removeTrailingZeroes(amount.toFixed(2)) + " L"
//           : "₹" + removeTrailingZeroes(amount.toFixed(2)) + " L";
//       } else if (amount >= 100000) {
//         // Convert to Lakhs
//         amount /= 100000;
//         return decimal
//           ? removeTrailingZeroes(amount.toFixed(2)) + " L"
//           : "₹" + removeTrailingZeroes(amount.toFixed(2)) + " L";
//       }
//     }

//     return formatWithCommas(amount); // Fallback to basic formatting
//   };

//   if (compactFormat) {
//     // If a compact format is provided, apply compact formatting
//     return formatCompactAmount(Amt, compactFormat);
//   } else {
//     // Otherwise, simply return formatted amount with commas
//     return formatWithCommas(Amt);
//   }
// };

export const formatAmount = (
  amount,
  decimal = null,
  removeFloat = null,
  compactFormat = "lakh" // If null, no compact formatting
) => {
  let Amt = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(Amt)) return decimal ? "0" : "₹0.00";

  const formatWithCommas = (amount) => {
    const options = {
      style: decimal ? "decimal" : "currency",
      minimumFractionDigits: decimal && !removeFloat ? 2 : 0,
      maximumFractionDigits: 2,
      currency: !decimal ? "INR" : undefined,
    };
    return amount.toLocaleString("en-IN", options); // Adds commas and currency
  };

  const formatCompactAmount = (amount, format) => {
    const removeTrailingZeroes = (value) => {
      return parseFloat(value).toString();
    };

    // Compact formatting logic for "lakh" and "crore"
    if (format === "lakh" || format === "10lakh" || format === "crore") {
      if (amount >= 10000000) {
        // Convert to Crore
        amount /= 10000000;
        return decimal
          ? removeTrailingZeroes(amount.toFixed(2)) + " Cr"
          : "₹" + removeTrailingZeroes(amount.toFixed(2)) + " Cr";
      } else if (amount >= 100000) {
        // Convert to Lakhs
        amount /= 100000;
        // Round the amount to 2 decimal places, ensuring 2 decimal places even if the value is a whole number
        const roundedAmount = amount.toFixed(2); // This ensures 2 decimals like 1.90, 1.00, etc.

        // Display as Lakh with no decimals if it's a whole number (like ₹10 L)
        if (parseFloat(roundedAmount) % 1 === 0) {
          return decimal
            ? roundedAmount.split(".")[0] + " L"
            : "₹" + roundedAmount.split(".")[0] + " L";
        }

        // Otherwise, show 2 decimal places
        return decimal ? roundedAmount + " L" : "₹" + roundedAmount + " L";
      }
    }

    // If no compact formatting is requested, fall back to standard formatting
    return formatWithCommas(amount);
  };

  if (compactFormat) {
    // Apply compact formatting if provided
    return formatCompactAmount(Amt, compactFormat);
  } else {
    // Otherwise, simply return formatted amount with commas
    return formatWithCommas(Amt);
  }
};

export const getDecimalWithRupeeSymbol = (amount) => {
  let Amt = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(Amt)) return "₹0";

  const integerPart = Math.floor(Amt);
  return `₹${integerPart}`;
};

export const formatDateTime = (value) => {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

export const formatDate = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[0];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatPaymentMode = (mode) => {
  if (!mode) return "N/A";
  
  return mode
    .replace(/[_-]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const trimmed = word.trim();
      if (trimmed.length <= 3) {
        return trimmed.toUpperCase();
      }
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    })
    .join(" ");
};

export const getCodeFromUrl = () => {
  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  let subdomain = url.hostname.split(".")[0];
  if (subdomain === "localhost") {
    subdomain = "chem";
  }
  return subdomain;
};

export const getNameAbbrevation = (name) => {
  return name
    ? name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase()) // Get the first letter of each word and capitalize it
        .join("") // Join the first letters together to form the initials
    : "A";
};

// export const formatAmountRounded = (
//   amount,
//   decimal,
//   removeFloat,
//   compactFormat // lakh, 10lakh, crore
// ) => {
//   let Amt = typeof amount === "string" ? parseFloat(amount) : amount;

//   if (isNaN(Amt || amount)) return decimal ? "0" : "₹0.00";

//   const isCompactAmount = (finalAmount, format) => {
//     const options = {
//       style: decimal ? "decimal" : "currency",
//       minimumFractionDigits: 0, // Ensure no decimal places
//       maximumFractionDigits: 0, // Ensure no decimal places
//       currency: !decimal ? "INR" : undefined,
//     };

//     if (format) {
//       let croreCondition =
//         (format === "crore" || format === "10lakh" || format === "lakh") &&
//         amount >= 10000000;
//       let tenlakhCondition =
//         (format === "10lakh" || format === "lakh") && amount >= 1000000;
//       let lakhCondition = format === "lakh" && amount >= 100000;

//       if (croreCondition) {
//         // Convert to Crores
//         amount /= 10000000;
//         amount = Math.round(amount);
//         return decimal ? amount + " Cr" : "₹" + amount + " Cr";
//       } else if (tenlakhCondition) {
//         // Convert to Lakhs if more than 10 Lakhs
//         amount /= 1000000;
//         amount = Math.round(amount);
//         return decimal ? amount + " L" : "₹" + amount + " L";
//       } else if (lakhCondition) {
//         // Convert to Lakhs if more than 1 Lakh
//         amount /= 100000;
//         amount = Math.round(amount);
//         return decimal ? amount + " L" : "₹" + amount + " L";
//       }
//     }

//     finalAmount = Math.round(finalAmount); // Round off to the nearest integer

//     if (decimal) return finalAmount?.toLocaleString("en-IN", options);

//     return finalAmount?.toLocaleString("en-IN", options);
//   };

//   // Two options for more control when to use compact amount
//   if (compactFormat) {
//     return isCompactAmount(Amt, compactFormat);
//   } else {
//     return isCompactAmount(Amt);
//   }
// };

// export function useBlocker(when) {
//   const navigate = useNavigate();

//   useEffect(() => {
//     if (!when) return;

//     const unblock = navigate.block((tx) => {
//       const confirmNavigation = window.confirm("Click on Logout to go out");
//       if (confirmNavigation) {
//         unblock();
//         tx.retry();
//       }
//     });

//     return unblock;
//   }, [when, navigate]);
// }

const statusStyles = {
  RECOMMENDED: { backgroundColor: "#2461FF", color: "#fff" },
  POPULAR: { backgroundColor: "#FFA9D8", color: "#fff" },
  PAYMENT_SUCCESS: { backgroundColor: "#DAF4F0", color: "#0AB39C" },
  PAYMENT_FAILED: { backgroundColor: "#F8D7DA", color: "#DC3545" },
  SUCCESS: { backgroundColor: "#DAF4F0", color: "#0AB39C" },
  PAID: { backgroundColor: "#DAF4F0", color: "#0AB39C" },
  ACCEPTED: { backgroundColor: "#DAF4F0", color: "#0AB39C" },
  REJECTED: { backgroundColor: "#F8D7DA", color: "#DC3545" },
  PROPOSAL_PENDING: {
    backgroundColor: "transparent",
    color: "#ea966d",
    padding: "0",
    fontSize: "12px",
    textTransform: "capitalize",
  },
  PAYMENT_PENDING: { backgroundColor: "#FFF9E0", color: "#FFCC00" },
  PENDING: { backgroundColor: "#FFF9E0", color: "#FFCC00" },
  CANCELLED: { backgroundColor: "#F8D7DA", color: "#DC3545" },
  COMPLETED: { backgroundColor: "#DAF4F0", color: "#0AB39C" },
  AWAITED_UNDERWRITING: {
    backgroundColor: "#FFF9E0",
    color: "#ea966d ",
  },
  ISSUED: { backgroundColor: "#DAF4F0", color: "#0AB39C" },
  DETAILS_AWAITED: { backgroundColor: "#FFF9E0", color: "#FFCC00" },
};

export const renderStatus = (status) => {
  const style = statusStyles[status?.toUpperCase()] || {
    backgroundColor: "#f0f0f0",
    color: "#000",
  };

  return (
    <div
      style={{
        backgroundColor: "#f0f0f0",
        color: "#000",
        fontWeight: "bold",
        padding: "5px",
        borderRadius: "5px",
        display: "flex",
        alignItems: "center",
        ...statusStyles[status?.toUpperCase()],
        // backgroundColor: style.backgroundColor,
        // color: style.color,
      }}
    >
      {status?.replace(/_/g, " ")?.toUpperCase()}
    </div>
  );
};

export const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    fontSize: "14px",
    height: "20px",
    // borderRadius: "10px",
    boxShadow: "none",
    // borderColor: "#C7222A",
    width: "200px",
    // "&:hover": {
    //   border: "0.2px solid #c7222a",
    // },
    // "&:focus": {
    //   border: "0.2px solid #c7222a",
    // },
  }),
  // dropdownIndicator: (provided) => ({
  //   ...provided,
  //   color: "#c7222a",
  // }),
  // indicatorSeparator: (provided) => ({
  //   ...provided,
  //   backgroundColor: "#c7222a",
  // }),
  placeholder: (provided) => ({
    ...provided,
    color: "#ababab",
    fontSize: "12px",
  }),
};

export const customSortStyles = {
  control: (provided) => ({
    ...provided,
    fontSize: "14px",
    height: "20px",
    borderRadius: "6px",
    boxShadow: "none",
    borderColor: "#616466",
    width: "200px",
    "&:hover": {
      border: "0.2px solid #616466",
    },
    // "&:focus": {
    //   border: "0.2px solid #c7222a",
    // },
  }),
  // dropdownIndicator: (provided) => ({
  //   ...provided,
  //   color: "#c7222a",
  // }),
  // indicatorSeparator: (provided) => ({
  //   ...provided,
  //   backgroundColor: "#c7222a",
  // }),
  placeholder: (provided) => ({
    ...provided,
    color: "#494B4D",
    fontSize: "13px",
  }),
};

export const setRedirectParams = (path) => {
  const redirectTo = localStorage.getItem("redirectTo");
  if (!redirectTo) {
    localStorage.setItem("redirectTo", path);
  }
};

export const getRedirectParams = (navigate) => {
  const redirectTo = localStorage.getItem("redirectTo");
  if (redirectTo) {
    navigate(redirectTo);
    localStorage.removeItem("redirectTo");
    return true;
  }
  return false;
};

export const downloadFileFromURL = (url, filename) => {
  fetch(url, {
    responseType: "blob",
  })
    .then((response) => {
      if (!response.ok) {
        console.error(`Error! Response status: ${response.status}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "download";
      link.style.display = "none";
      document.body.appendChild(link);
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 0);
    });
};

export const getParamsFromURL = async (location, key = null) => {
  // Extract search parameters from the location object
  const searchParams = new URLSearchParams(location.search);
  // Get the encrypted value from the "code" parameter
  const encryptedValue = searchParams.get("code");

  if (!encryptedValue) {
    return null;
  }

  try {
    // Decrypt the "code" parameter value asynchronously
    const decryptedParamsString = await Decrypt(encryptedValue).then(
      (decryptedData) => {
        return decryptedData;
      }
    );
    // Parse the decrypted string into URLSearchParams
    const params = new URLSearchParams(decryptedParamsString);

    if (key) {
      // If a key is specified, return its value or null if it doesn't exist
      return params.get(key) || null;
    } else {
      // If no key is specified, return all keys and their values as an object
      const allParams = {};
      params.forEach((value, paramKey) => {
        allParams[paramKey] = value;
      });
      return allParams;
    }
  } catch (error) {
    console.error(`Error decrypting "code":`, error);
    return null;
  }
};

export const setParamsInURL = async (location, paramsObject) => {
  if (!paramsObject) {
    return null;
  }
  try {
    const currentParams = new URLSearchParams(location.search);

    // Add each key-value pair to the params object
    for (const [key, value] of Object.entries(paramsObject)) {
      currentParams.set(key, value);
    }

    const encryptedValue = await Encrypt(currentParams.toString());
    return `code=${encryptedValue.toString()}`;
  } catch (error) {
    console.error("Error encrypting parameters:", error);
    return null;
  }
};

export const rmNumber = "7045021897";

export const rmEmail = "support@policyera.in";

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return isMobile;
};

export const renderOrderStatus = (proposalFilled) => {
  if (proposalFilled === 0) {
    return renderStatus("PROPOSAL_PENDING");
  } else {
    return null;
  }
};

export const handleConditionalRedirect = (returnUrl, fallbackRoute, nav) => {
  const currentOrigin = window.location.origin;

  try {
    const parsedUrl = new URL(returnUrl);
    const returnOrigin = parsedUrl.origin;

    if (currentOrigin !== returnOrigin) {
      window.location.href = `${returnUrl}${fallbackRoute}`;
    } else {
      nav(fallbackRoute);
    }
  } catch (error) {
    console.error("Invalid returnUrl:", error);
    nav(fallbackRoute);
  }
};

export const modifyEncodedURLParams = async (
  href,
  key,
  action,
  value = null
) => {
  try {
    const url = new URL(href);
    const searchParams = new URLSearchParams(url.search);
    const encryptedValue = searchParams.get("code");

    if (!encryptedValue) {
      return href;
    }

    const decryptedParamsString = await Decrypt(encryptedValue).then(
      (decryptedData) => {
        return decryptedData;
      }
    );
    // Decode the existing parameters
    const params = new URLSearchParams(decryptedParamsString);

    if (action === "add" && value !== null) {
      params.set(key, value);
    } else if (action === "remove") {
      params.delete(key);
    }

    // Encrypt the updated params
    const encryptedUpdatedValue = await Encrypt(params.toString());
    searchParams.set("code", encryptedUpdatedValue);
    console.log(searchParams.get("code"));
    // Update URL and return
    // url.search = searchParams.toString();
    url.search = decodeURIComponent(searchParams.toString()); // ✅ Decodes %3D back to "="
    return url.toString();
  } catch (error) {
    console.error("Error modifying encoded URL parameters:", error);
    return href;
  }
};

  export const renderLabel = (label, description) => (
        <div className="d-flex justify-content-between align-items-center">
          <span>{label}</span>
          {description && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`tooltip-desc-${label?.toString().replace(/[^a-zA-Z0-9]/g, '-')}`}>{description}</Tooltip>}
            >
              <span className="ms-1 align-middle" style={{ cursor: "pointer", display: "inline-block" }}>
                <InfoIcon />
              </span>
            </OverlayTrigger>
          )}
        </div>
      );
